// Kompakt bekräftelsemodal som visas innan ett e-postutskick från ComposeEmailModal
// (medlemsregistret) skickas. Visar avsändare + mottagar-räkning + förhandsgranskning
// med Dator/Mobil-tabs så användaren kan dubbelkolla innan utskicket faktiskt går iväg.

import { DesktopWindows, PhoneIphone, Send } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Tab, Tabs, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GroupMessageEmailTemplateView } from './groupMessageEmailTemplateView';

type Props = {
  open: boolean;
  loading: boolean;
  recipientsCount: number;
  onClose: () => void;
  onConfirm: () => void;
};

type ViewMode = 'desktop' | 'mobile';

const MOBILE_VIEWPORT_WIDTH = 360;

export const ComposeEmailConfirmDialog = (props: Props) => {
  const { open, loading, recipientsCount, onClose, onConfirm } = props;
  const { t } = useTranslation(['groupMessage', 'common']);
  const { watch } = useFormContext();
  const [viewMode, setViewMode] = useState<ViewMode>('desktop');
  const subject = watch('subject') as string | undefined;
  const sender = watch('sender') as string | undefined;
  const senderTitle = watch('senderTitle') as string | undefined;
  const body = watch('body') as string | undefined;

  const SummaryRow = ({ label, value }: { label: string; value: string }) => (
    <Box>
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      maxWidth='md'
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>{t('groupMessage:create.confirmSendNow')}</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ minHeight: '24rem' }}>
          {/* Vänster: sammanfattning */}
          <Stack
            spacing={1.5}
            sx={{ p: 2, width: { xs: '100%', sm: 260 }, borderRight: { sm: 1 }, borderBottom: { xs: 1, sm: 0 }, borderColor: 'divider' }}
          >
            <SummaryRow
              label={t('groupMessage:create.summary.recipients') as string}
              value={`${recipientsCount} ${recipientsCount === 1 ? t('groupMessage:selection.recipient') : t('groupMessage:selection.recipients')}`}
            />
            <SummaryRow label={t('groupMessage:create.summary.subject') as string} value={subject || '-'} />
            <SummaryRow label={t('groupMessage:create.summary.senderTitle') as string} value={senderTitle || '-'} />
            <SummaryRow label={t('groupMessage:create.summary.sender') as string} value={sender || '-'} />
          </Stack>
          {/* Höger: preview med Dator/Mobil-tabs */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '24rem' }}>
            <Tabs
              value={viewMode}
              onChange={(_, value: ViewMode) => setViewMode(value)}
              variant='fullWidth'
              sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
            >
              <Tab
                value='desktop'
                icon={<DesktopWindows fontSize='small' />}
                iconPosition='start'
                label={t('groupMessage:emailTemplates.previewDesktop')}
                sx={{ minHeight: 40 }}
              />
              <Tab
                value='mobile'
                icon={<PhoneIphone fontSize='small' />}
                iconPosition='start'
                label={t('groupMessage:emailTemplates.previewMobile')}
                sx={{ minHeight: 40 }}
              />
            </Tabs>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'stretch',
                bgcolor: (theme) => theme.palette.grey[100],
                p: viewMode === 'mobile' ? 2 : 0,
                overflow: 'auto',
              }}
            >
              {viewMode === 'desktop' ? (
                <Box sx={{ width: '100%', height: '100%', minHeight: '22rem' }}>
                  <GroupMessageEmailTemplateView body={body || ''} height='100%' />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: MOBILE_VIEWPORT_WIDTH,
                    maxWidth: '100%',
                    height: '100%',
                    minHeight: '22rem',
                    borderRadius: 3,
                    border: '6px solid',
                    borderColor: 'grey.800',
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                    boxShadow: 3,
                  }}
                >
                  <GroupMessageEmailTemplateView body={body || ''} height='100%' />
                </Box>
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant='outlined' onClick={onClose} disabled={loading}>
          {t('common:button.cancel')}
        </Button>
        <LoadingButton variant='contained' onClick={onConfirm} loading={loading} endIcon={<Send fontSize='small' />}>
          {t('common:button.send')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
